import type { WriteOffDiscrepancy } from '@shared/api';
import { fmtNum } from '@shared/lib/smetaLabels';

export function statusTone(status: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'processing':
      return 'info';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function statusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return 'готово';
    case 'processing':
      return 'анализ…';
    case 'pending':
      return 'в очереди';
    case 'failed':
      return 'ошибка';
    default:
      return status;
  }
}

export function discrTone(sev: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  switch (sev) {
    case 'critical':
      return 'danger';
    case 'major':
      return 'warning';
    case 'minor':
      return 'info';
    default:
      return 'neutral';
  }
}

export function severityLabel(sev: string): string {
  switch (sev) {
    case 'critical':
      return 'критично';
    case 'major':
      return 'существенно';
    case 'minor':
      return 'незначительно';
    default:
      return sev;
  }
}

export function kindLabel(kind: string): string {
  switch (kind) {
    case 'overspend':
      return 'перерасход';
    case 'saving':
      return 'экономия';
    case 'fact_no_norm':
      return 'без основания';
    case 'norm_no_fact':
      return 'без факта';
    case 'unit_mismatch':
      return 'единицы несовместимы';
    default:
      return kind;
  }
}

/** Позиция КС-2, по которой нормативного расхода нет или он посчитан с оговоркой. */
export interface NormNote {
  code: string;
  name: string;
  reason: string;
  message: string;
}

/** Заметки о нормативе из `summary.norm_notes`; у старых расчётов — пусто. */
export function extractNormNotes(summary: unknown): NormNote[] {
  if (!summary || typeof summary !== 'object') return [];
  const raw = (summary as Record<string, unknown>).norm_notes;
  if (!Array.isArray(raw)) return [];
  const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  return raw
    .filter((v): v is Record<string, unknown> => !!v && typeof v === 'object')
    .map((v) => ({
      code: text(v.code),
      name: text(v.name),
      reason: text(v.reason),
      message: text(v.message),
    }))
    .filter((n) => n.message.length > 0);
}

export function fmtDelta(d: WriteOffDiscrepancy): string {
  if (d.delta_quantity === null || d.delta_quantity === undefined) return '—';
  const pct =
    d.delta_pct !== null && d.delta_pct !== undefined ? ` (${d.delta_pct.toFixed(1)}%)` : '';
  return `${fmtNum(d.delta_quantity)}${pct}`;
}

export function extractFailedSources(summary: unknown): {
  failedSources: string[];
  totalFiles: number | null;
  hasUnparsedKs2: boolean;
} {
  if (!summary || typeof summary !== 'object') {
    return { failedSources: [], totalFiles: null, hasUnparsedKs2: false };
  }
  const rec = summary as Record<string, unknown>;
  const rawList = Array.isArray(rec.failed_sources) ? rec.failed_sources : [];
  const failedSources = rawList
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map((v) => v.trim());
  const totalFiles =
    typeof rec.files === 'number' && Number.isFinite(rec.files) ? rec.files : null;
  const ks2FailedCount =
    typeof rec.ks2_failed_count === 'number' && Number.isFinite(rec.ks2_failed_count)
      ? rec.ks2_failed_count
      : null;
  // Для новых расчётов блокируем отправку претензии/риска именно при неразобранных
  // КС-2 (нормативы неполные); для прежних записей без ks2_failed_count — консервативно
  // при наличии любых failed_sources.
  const hasUnparsedKs2 = ks2FailedCount !== null ? ks2FailedCount > 0 : failedSources.length > 0;
  return { failedSources, totalFiles, hasUnparsedKs2 };
}
